/**
 * App Layout Component
 * Shared layout with navigation for Echo application
 * Uses FTI Design System components
 */

import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useProviderLogout } from '../auth/hooks';
import { useEchoUser } from '../hooks/useEchoUser';
import {
  BasicHeader,
  Sidebar,
  Button,
  Flex,
  Icon,
  useTheme,
  spacing,
  fontSize,
} from '@fti/design-system';

// Type definitions for Sidebar navigation
interface NavigationItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}

interface NavigationGroup {
  id: string;
  label: string;
  defaultExpanded?: boolean;
  items: NavigationItem[];
}

export function AppLayout() {
  const auth = useAuth();
  const { user, isAdmin } = useEchoUser();
  const { signoutRedirect } = useProviderLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Main navigation items using design system format
  // Available icons: arrow-left, arrow-right, arrow-up, arrow-down, chevron-left, chevron-right,
  // chevron-up, chevron-down, home, menu, check, close, plus, minus, edit, trash, save, copy,
  // search, settings, user, users, mail, bell, calendar, clock, eye, eye-off, filter, download,
  // upload, refresh, logout, login, info, warning, error, success, help
  const mainNavItems: NavigationItem[] = [
    {
      id: 'welcome',
      label: 'Welcome',
      icon: <Icon name="home" size="sm" />,
      active: isActive('/'),
      onClick: () => navigate('/'),
    },
    {
      id: 'transcribe',
      label: 'Transcribe and Summarize from URL',
      icon: <Icon name="search" size="sm" />,
      active: isActive('/transcribe'),
      onClick: () => navigate('/transcribe'),
    },
    {
      id: 'upload',
      label: 'Transcribe and Summarize from File',
      icon: <Icon name="upload" size="sm" />,
      active: isActive('/upload'),
      onClick: () => navigate('/upload'),
    },
    {
      id: 'view',
      label: 'View Transcriptions',
      icon: <Icon name="eye" size="sm" />,
      active: isActive('/view'),
      onClick: () => navigate('/view'),
    },
    {
      id: 'history',
      label: 'Workflow History',
      icon: <Icon name="clock" size="sm" />,
      active: isActive('/history'),
      onClick: () => navigate('/history'),
    },
  ];

  // Admin navigation items
  const adminNavItems: NavigationItem[] = [
    {
      id: 'matters',
      label: 'Matter Code Manager',
      icon: <Icon name="settings" size="sm" />,
      active: isActive('/matters'),
      onClick: () => navigate('/matters'),
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <Icon name="users" size="sm" />,
      active: isActive('/users'),
      onClick: () => navigate('/users'),
    },
    {
      id: 'billing',
      label: 'Billing Manager',
      icon: <Icon name="filter" size="sm" />,
      active: isActive('/billing'),
      onClick: () => navigate('/billing'),
    },
    {
      id: 'github-monitor',
      label: 'GitHub Monitor',
      icon: <Icon name="refresh" size="sm" />,
      active: isActive('/github-monitor'),
      onClick: () => navigate('/github-monitor'),
    },
  ];

  // Build sidebar groups
  const sidebarGroups: NavigationGroup[] = [
    {
      id: 'main',
      label: 'Main',
      defaultExpanded: true,
      items: mainNavItems,
    },
  ];

  if (isAdmin) {
    sidebarGroups.push({
      id: 'admin',
      label: 'Admin',
      defaultExpanded: true,
      items: adminNavItems,
    });
  }

  // Header actions (right side)
  const headerActions = (
    <Flex align="center" gap="sm">
      {auth.isAuthenticated && user && (
        <>
          <span
            style={{
              fontSize: fontSize.sm,
              color: theme.colors.text.secondary,
            }}
          >
            {user.email}
            {isAdmin && (
              <span
                style={{
                  marginLeft: spacing.xs,
                  color: theme.colors.semantic.info.default,
                  fontWeight: 500,
                }}
              >
                (Admin)
              </span>
            )}
          </span>
          <Button
            variant="outline"
            color="error"
            size="sm"
            onClick={() => signoutRedirect()}
          >
            Sign Out
          </Button>
        </>
      )}
    </Flex>
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.colors.background.default,
      }}
    >
      {/* Header using FTI Design System */}
      <BasicHeader
        items={[]}
        logo={{
          size: 'medium',
          variant: 'default',
          onClick: () => navigate('/'),
        }}
        actions={headerActions}
      />

      {/* Main Content Area */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, height: 'calc(100vh - 64px)' }}>
        {/* Sidebar Navigation using FTI Design System */}
        <Sidebar groups={sidebarGroups} style={{ height: '100vh' }} />

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            padding: spacing.lg,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </main>
      </div>

    </div>
  );
}

export default AppLayout;